import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { pixel } from '../core/pixel';
import './LandingV2.css';

// ── Copy ──────────────────────────────────────────────────────────────────────

const MONTHLY = { basic: 99, pro: 179 };
const ANNUAL  = { basic: 83, pro: 149 };

// ── Icons (60-30-10 Branding) ──────────────────────────────────────────────────

function IconGhost() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 10h.01M15 10h.01M12 2a8 8 0 0 0-8 8v12l3-3 2.5 2.5L12 19l2.5 2.5L17 19l3 3V10a8 8 0 0 0-8-8z" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconMegaphone() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m3 11 18-5v12L3 13v-2z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

function IconUnanswered() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function IconPriceTag() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
      <path d="M7 7h.01" />
    </svg>
  );
}

function IconMoon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

function IconSnowflake() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="m17 7-5 5-5-5" />
      <path d="m17 17-5-5-5 5" />
    </svg>
  );
}

function IconTarget() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

const PROBLEM_CARDS = [
  { icon: IconGhost, title: 'Pacientes fantasma', desc: 'Preguntan precio, dicen "lo pienso" y nunca vuelven. Nadie los llama de vuelta.' },
  { icon: IconClock, title: 'Follow-up imposible', desc: 'Tu equipo no tiene tiempo de perseguir cada conversación fría. Las oportunidades mueren solas.' },
  { icon: IconMegaphone, title: 'Publicidad desperdiciada', desc: 'Pagas para atraerlos por WhatsApp, pero se van sin agendar. El ciclo se repite.' },
];

const DETECT_CARDS = [
  { icon: IconUnanswered, title: 'Conversaciones sin seguimiento', desc: 'Pacientes que escribieron y nunca recibieron una segunda respuesta.' },
  { icon: IconPriceTag, title: 'Pacientes que preguntaron precios', desc: 'Interesados que pidieron info pero no confirmaron la cita.' },
  { icon: IconMoon, title: 'Consultas fuera de horario', desc: 'Mensajes que llegaron cuando nadie estaba disponible para responder.' },
  { icon: IconSnowflake, title: 'Conversaciones que se enfriaron', desc: 'El paciente dejó de responder antes de confirmar.' },
  { icon: IconTarget, title: 'Alta intención de agendar', desc: 'Conversaciones donde el paciente mostró interés real pero no terminó de confirmar.' },
];

const COMPARE_ROWS: { label: string; manual: boolean; bot: boolean; diana: boolean }[] = [
  { label: 'Responde en segundos, 24/7',                     manual: false, bot: true,  diana: true },
  { label: 'Detecta pacientes sin seguimiento o enfriados',   manual: false, bot: false, diana: true },
  { label: 'Reactiva conversaciones sin que nadie lo pida',   manual: false, bot: false, diana: true },
  { label: 'Entiende precios y servicios reales de tu clínica', manual: true, bot: false, diana: true },
  { label: 'Agenda, reprograma y cancela citas solo',         manual: false, bot: false, diana: true },
  { label: 'Sin conocimientos técnicos para configurarlo',    manual: true,  bot: false, diana: true },
];

const STEPS = [
  { n: '1', title: 'Conecta tu WhatsApp', desc: 'Escaneas un QR en segundos. Tus pacientes siguen escribiendo al mismo número.' },
  { n: '2', title: 'Diana analiza', desc: 'Detecta oportunidades en conversaciones recientes: sin seguimiento, interesados fríos, consultas sin respuesta.' },
  { n: '3', title: 'Configura tu clínica', desc: 'Agrega servicios, precios y horarios. Diana aprende cómo funciona tu negocio.' },
  { n: '4', title: 'Diana trabaja sola', desc: 'Responde, agenda, reprograma y reactiva a quienes se quedaron en silencio.' },
];

const BASIC_FEATS = [
  'Respuestas automáticas 24/7',
  'Agenda inteligente y disponibilidad en tiempo real',
  'Gestión de citas: agendar, reagendar y cancelar',
  'Hasta 2 usuarios',
  'Hasta 800 contactos activos al mes',
];
const PRO_FEATS = [
  'Todo lo del Plan Básico',
  'Seguimiento automático a interesados',
  'Reactivación de conversaciones inactivas',
  'Promociones dinámicas en conversación',
  'Optimización de anuncios con citas reales',
  'Solicitud automática de reseñas',
  'Reportes de impacto y ROI',
  'Hasta 1,500 contactos activos al mes',
];

const FAQS = [
  { q: '¿Diana funciona para cualquier tipo de negocio?', a: 'No. Diana está entrenada específicamente para clínicas dentales — conoce el vocabulario, los servicios y las preguntas típicas de pacientes dentales. Si conectas el WhatsApp de otro tipo de negocio, Diana no va a responder correctamente porque no está diseñada para eso.' },
  { q: '¿Necesito cambiar mi número de WhatsApp?', a: 'No. Diana se conecta al número que ya usas en tu clínica mediante un código QR. Tus pacientes siguen escribiendo al mismo número de siempre.' },
  { q: '¿Los pacientes saben que hablan con una IA?', a: 'Diana se presenta como asistente virtual de la clínica y mantiene un tono claro, natural y profesional. Los mensajes son personalizados — la mayoría de pacientes no nota la diferencia.' },
  { q: '¿Qué pasa si quiero responder yo mismo?', a: 'Puedes pausar a Diana en cualquier conversación e intervenir manualmente cuando lo necesites. Diana vuelve a activarse automáticamente cuando terminas.' },
  { q: '¿Puedo cancelar en cualquier momento?', a: 'Sí. Sin contratos de permanencia, sin penalidades. Cancelas desde tu panel en un clic y no se te vuelve a cobrar.' },
  { q: '¿Qué pasa cuando termina la prueba?', a: 'Cuando termina, las funciones de seguimiento y recuperación se pausan. Para mantener todo activo, activas un plan. Tus datos y configuración se conservan.' },
  { q: '¿En qué idiomas funciona Diana?', a: 'Diana funciona en español de forma nativa. Entiende el vocabulario dental y las expresiones reales de pacientes en LATAM y el Caribe.' },
];

// ── Animated Chat ─────────────────────────────────────────────────────────────

type Msg = { id: number; type: 'in' | 'out' | 'typing' | 'speed'; text?: string; time?: string };

const CHAT_SEQUENCE: Omit<Msg, 'id'>[] = [
  { type: 'in',     text: 'Hola, ¿cuánto cuesta la limpieza dental?', time: '9:12 AM' },
  { type: 'typing' },
  { type: 'speed' },
  { type: 'out',    text: 'Hola 😊 La limpieza tiene un valor de $80. ¿Te gustaría agendar esta semana?', time: '9:12 AM' },
  { type: 'in',     text: 'Lo voy a pensar...', time: '9:13 AM' },
  { type: 'out',    text: '¡Hola de nuevo! 👋 Aún tengo disponibilidad esta semana. ¿Quieres que te reserve un horario?', time: '10:00 AM · día siguiente' },
  { type: 'in',     text: 'Sí, ¿mañana a las 10?', time: '10:04 AM' },
  { type: 'typing' },
  { type: 'out',    text: '¡Perfecto! Cita confirmada para mañana a las 10:30 AM ✅\nTe enviaré un recordatorio. ¡Te esperamos!', time: '10:04 AM' },
];

const DELAYS = [0, 900, 1800, 2800, 4800, 7500, 9800, 11000, 12200];
const TOTAL_DURATION = 15000;

function AnimatedChat() {
  const [visible, setVisible] = useState<Msg[]>([]);
  const chatRef = useRef<HTMLDivElement>(null);
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAll = () => { timeouts.current.forEach(clearTimeout); timeouts.current = []; };

  const startSequence = useCallback(() => {
    setVisible([]);
    clearAll();

    CHAT_SEQUENCE.forEach((msg, i) => {
      const t = setTimeout(() => {
        setVisible(prev => {
          // Remove typing indicator when next real message comes
          const filtered = msg.type !== 'typing' ? prev.filter(m => m.type !== 'typing') : prev;
          return [...filtered, { ...msg, id: Date.now() + i }];
        });
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
      }, DELAYS[i]);
      timeouts.current.push(t);
    });

    const restart = setTimeout(() => startSequence(), TOTAL_DURATION);
    timeouts.current.push(restart);
  }, []);

  useEffect(() => {
    startSequence();
    return clearAll;
  }, [startSequence]);

  return (
    <div className="v2-phone-wrap">
      {/* Industry stat badge */}
      <div className="v2-live-badge" style={{ marginBottom: 14 }}>
        <span className="v2-live-dot" />
        <span>En promedio, <strong>4 de cada 10</strong> conversaciones de WhatsApp en clínicas dentales quedan sin seguimiento*</span>
      </div>

      <div className="v2-phone-device">
        <div className="v2-phone-screen">
          {/* Header */}
          <div className="v2-phone-header">
            <div className="v2-phone-avatar">D</div>
            <div className="v2-phone-header-info">
              <div className="v2-phone-header-name">Diana · Asistente Dental</div>
              <div className="v2-phone-header-status">● en línea</div>
            </div>
            <div className="v2-phone-speed">{'< 3 seg'}</div>
          </div>

          {/* Chat */}
          <div className="v2-phone-chat" ref={chatRef}>
            {visible.map(msg => {
              if (msg.type === 'typing') {
                return (
                  <div key={msg.id} className="v2-typing-indicator">
                    <span className="v2-typing-dot" />
                    <span className="v2-typing-dot" />
                    <span className="v2-typing-dot" />
                  </div>
                );
              }
              if (msg.type === 'speed') {
                return <div key={msg.id} className="v2-speed-tag">⚡ Respondido en 2 segundos</div>;
              }
              return (
                <div key={msg.id} className={`v2-bubble ${msg.type}`}>
                  {msg.text}
                  {msg.time && <small className="v2-bubble-time">{msg.time}</small>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 10, textAlign: 'center' }}>
        *Estimado en base a clínicas dentales en RD y LATAM. Los resultados individuales varían.
      </p>
    </div>
  );
}

// ── Recovery Calculator ───────────────────────────────────────────────────────

// Follow-up ratio grows with volume — a small clinic keeps up reasonably well,
// but a busy one can't scale its team, so a bigger share of conversations go
// unanswered. Saturating curve (not linear): most of the growth happens early,
// then levels off, matching how quickly a team gets overwhelmed.
// Tuned so the default slider position (60/week) lands on ~40% — matching the
// "4 de cada 10" stat quoted in the hero.
function followUpRatio(convos: number) {
  return 0.20 + 0.39 * Math.sqrt(Math.max(0, convos - 10) / 190);
}

function riskBand(score: number): { label: string; color: string } {
  if (score < 25) return { label: 'Crítico', color: 'var(--danger)' };
  if (score < 45) return { label: 'En Riesgo', color: 'var(--gold)' };
  return { label: 'Moderado', color: 'var(--accent)' };
}

function RecoveryCalc() {
  const [convos, setConvos] = useState(60);

  const ratio           = followUpRatio(convos);
  const sinSeguimiento  = Math.round(convos * ratio);
  const altaIntencion   = Math.round(convos * 0.28);
  const recuperables    = Math.round(sinSeguimiento * 0.45);
  const score = Math.round(Math.max(15, Math.min(65, 100 - ratio * 188)));
  const risk  = riskBand(score);

  return (
    <div className="v2-calc-box">
      {/* Left: slider */}
      <div>
        <div className="v2-calc-label" style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          Mueve el slider para estimar tus oportunidades
        </div>
        <div className="v2-calc-value">{convos}</div>
        <div className="v2-calc-sub" style={{ color: 'var(--text-secondary)' }}>conversaciones por semana en tu clínica</div>

        <input
          type="range" min="10" max="200" value={convos}
          onChange={e => setConvos(Number(e.target.value))}
          className="v2-slider"
          aria-label="Conversaciones por semana en tu clínica"
          aria-valuetext={`${convos} conversaciones por semana`}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-faint)' }}>
          <span>10 / semana</span>
          <span>200 / semana</span>
        </div>

        <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, marginTop: 20 }}>
          Con <strong style={{ color: 'var(--text)' }}>{convos} conversaciones semanales</strong>, tu clínica podría estar dejando ir hasta{' '}
          <strong style={{ color: 'var(--danger)' }}>{recuperables} oportunidades</strong> cada semana por falta de seguimiento. Diana puede retomar esas conversaciones automáticamente.
        </p>
      </div>

      {/* Right: metrics */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 52, fontWeight: 900, color: risk.color, lineHeight: 1 }}>{score}</span>
          <span style={{ fontSize: 16, color: 'var(--text-faint)', fontWeight: 600 }}>/100</span>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: risk.color, marginBottom: 6 }}>
          Diana Recovery Score™ estimado · {risk.label}
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 22 }}>
          Qué tan bien tu clínica responde y le da seguimiento a los pacientes que escriben por WhatsApp. Mientras más bajo, más citas se te están escapando.
        </p>

        <div className="v2-calc-metrics">
          <div className="v2-calc-metric">
            <span className="v2-calc-metric-label">Sin seguimiento esta semana</span>
            <span className="v2-calc-metric-value">{sinSeguimiento}</span>
          </div>
          <div className="v2-calc-metric">
            <span className="v2-calc-metric-label">Interesados que no confirmaron cita</span>
            <span className="v2-calc-metric-value">{altaIntencion}</span>
          </div>
          <div className="v2-calc-metric highlight-box">
            <span className="v2-calc-metric-label">Conversaciones que Diana puede retomar</span>
            <span className="v2-calc-metric-value highlight">{recuperables}</span>
          </div>
        </div>

        <p style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 14, lineHeight: 1.5 }}>
          Estimación basada en patrones promedio de clínicas dentales en RD y LATAM.
        </p>
      </div>
    </div>
  );
}

// ── FAQ Item ──────────────────────────────────────────────────────────────────

function FaqItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="v2-faq-item">
      <button className={`v2-faq-q ${open ? 'open' : ''}`} onClick={onToggle}>
        <span>{q}</span>
        <span className="v2-faq-chevron">⌄</span>
      </button>
      <div className={`v2-faq-a ${open ? 'open' : ''}`}>{a}</div>
    </div>
  );
}

// ── Animated Counter ──────────────────────────────────────────────────────────

function AnimCount({ to, suffix = '' }: { to: number; suffix?: string }) {
  // Arranca en el valor final (no en 0): el prerender para crawlers de IA
  // (GPTBot, ClaudeBot, PerplexityBot — ver scripts/prerender.mjs) captura
  // el HTML sin scrollear, así que el IntersectionObserver de abajo nunca
  // llega a disparar y el valor inicial queda congelado en el HTML estático.
  // Para un visitante real, el efecto de conteo sigue viéndose igual: el
  // observer resetea a 0 y anima hacia arriba en cuanto la sección entra
  // en pantalla.
  const [val, setVal] = useState(to);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      let start = 0;
      setVal(0);
      const step = () => {
        start += Math.ceil((to - start) / 8);
        if (start >= to) { setVal(to); return; }
        setVal(start);
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);

  return <span ref={ref}>{val}{suffix}</span>;
}

// ── Scroll Reveal Hook ────────────────────────────────────────────────────────

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.v2-reveal');
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.12 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function LandingV2() {
  const [scrolled, setScrolled]   = useState(false);
  const [billing, setBilling]     = useState<'monthly' | 'annual'>('monthly');
  const [openFaq, setOpenFaq]     = useState<number | null>(0);

  useReveal();

  useEffect(() => { pixel.viewContent(); }, []);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const handleCTA = () => pixel.addToCart();
  const prices = billing === 'annual' ? ANNUAL : MONTHLY;

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="v2">

      {/* ── AURORA BACKGROUND ─────────────────────────────────── */}
      <div className="v2-aurora" aria-hidden="true">
        <div className="v2-aurora-blob" />
        <div className="v2-aurora-blob" />
        <div className="v2-aurora-blob" />
      </div>

      {/* ── NAVBAR ──────────────────────────────────────────── */}
      <nav className={`v2-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="v2-nav-inner">
          <a href="#" className="v2-logo">
            <img src="/logo.webp" alt="Diana IA" width={34} height={34} />
          </a>
          <div className="v2-nav-links">
            <a href="#como-funciona" onClick={e => { e.preventDefault(); scrollTo('como-funciona'); }}>Cómo funciona</a>
            <a href="#precio" onClick={e => { e.preventDefault(); scrollTo('precio'); }}>Precio</a>
            <a href="#faq" onClick={e => { e.preventDefault(); scrollTo('faq'); }}>FAQ</a>
          </div>
          <div className="v2-nav-actions">
            <Link to="/login" className="v2-btn-ghost">
              <span className="v2-nav-text-full">Iniciar sesión</span>
              <span className="v2-nav-text-short">Entrar</span>
            </Link>
            <Link to="/register" className="v2-pulse-btn sm" onClick={handleCTA}>
              <span className="v2-nav-text-full">Analizar mi WhatsApp</span>
              <span className="v2-nav-text-short">Analizar</span>
            </Link>
          </div>
        </div>
      </nav>

      <main id="contenido">

      {/* ── HERO ────────────────────────────────────────────── */}
      <section className="v2-hero">
        <div className="v2-container">
          <div className="v2-hero-grid">

            {/* Copy — top (headline, always first) */}
            <div className="v2-hero-copy-top">
              <div className="v2-eyebrow v2-reveal">
                <span className="v2-eyebrow-dot" />
                Asistente de seguimiento automático · Clínicas dentales
              </div>

              <h1 className="v2-h1 v2-reveal v2-reveal-delay-1">
                Cada día se <span className="v2-aurora-text">pierden citas</span><br />
                por <span className="v2-aurora-text">falta de seguimiento</span><br />
                <em>en tu WhatsApp.</em>
              </h1>
            </div>

            {/* Chat Demo — shows right after the headline on mobile, beside the copy on desktop */}
            <div className="v2-hero-phone v2-reveal v2-reveal-delay-2">
              <AnimatedChat />
            </div>

            {/* Copy — rest (sub, CTAs, trust) */}
            <div className="v2-hero-copy-rest">
              <p className="v2-hero-sub v2-reveal v2-reveal-delay-2">
                Pacientes preguntan, dicen "lo pienso" o escriben fuera de horario
                — y nadie hace el seguimiento. Conecta tu WhatsApp Business en 2 minutos,
                Diana analiza tus conversaciones y empieza a hacer el seguimiento automáticamente.
                En promedio, <strong>3 a 8 citas adicionales al mes.</strong>
              </p>

              <div className="v2-hero-actions v2-reveal v2-reveal-delay-3">
                <Link to="/register" className="v2-pulse-btn lg" onClick={handleCTA}>
                  Conectar mi WhatsApp y ver el Score™
                </Link>
                <a
                  href="#como-funciona"
                  className="v2-btn-ghost"
                  onClick={e => { e.preventDefault(); scrollTo('como-funciona'); }}
                >
                  Ver cómo funciona →
                </a>
              </div>

              <p className="v2-hero-note v2-reveal v2-reveal-delay-3">
                Prueba completa durante <strong>14 días o 100 contactos únicos</strong>. Sin tarjeta de crédito.
              </p>
              <p className="v2-hero-note v2-reveal v2-reveal-delay-3" style={{ marginTop: 4 }}>
                🔒 Tus conversaciones y las de tus pacientes permanecen privadas. Solo tu clínica tiene acceso.
              </p>

              <div className="v2-trust-pills v2-reveal v2-reveal-delay-4">
                {[
                  'Responde en segundos',
                  'Seguimiento automático',
                  'WhatsApp nativo',
                  'Sin conocimientos técnicos',
                ].map(t => (
                  <span key={t} className="v2-trust-pill">
                    <span className="check">✓</span> {t}
                  </span>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── STATS BAND ──────────────────────────────────────── */}
      <section className="v2-stats-band">
        <div className="v2-container">
          <div className="v2-stats-grid">
            {[
              { val: 3, suffix: 's', label: 'Tiempo promedio de respuesta' },
              { val: 24, suffix: '/7', label: 'Disponibilidad de respuesta' },
              { val: 40, suffix: '%', label: 'Conversaciones sin seguimiento típico*', prefix: 'Hasta' },
              { val: 3, suffix: 'x', label: 'Más conversaciones retomadas*' },
            ].map(s => (
              <div key={s.label} className="v2-stat-cell">
                <div className="v2-stat-value">
                  {s.prefix ? s.prefix : '<'} <AnimCount to={s.val} suffix={s.suffix} />
                </div>
                <div className="v2-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-faint)', marginTop: 12 }}>
            *Estimaciones internas en base a clínicas dentales que usan seguimiento activo vs. sin seguimiento. No es un estudio de terceros; los resultados individuales varían.
          </p>
        </div>
      </section>

      {/* ── PROBLEMA ────────────────────────────────────────── */}
      <section className="v2-section">
        <div className="v2-container">
          <div className="v2-section-tag v2-reveal">El problema</div>
          <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">
            Tu WhatsApp está lleno<br />de oportunidades sin explotar
          </h2>
          <p className="v2-section-sub v2-reveal v2-reveal-delay-2">
            Cada semana, pacientes te escriben con intención real de agendar.
            Sin seguimiento automático, la mayoría no vuelve. La cita nunca ocurre.
          </p>
          <div className="v2-problem-grid">
            {PROBLEM_CARDS.map((c, i) => (
              <div key={c.title} className={`v2-problem-card v2-reveal v2-reveal-delay-${i + 1}`}>
                <div className="v2-icon-box">
                  <c.icon />
                </div>
                <div className="v2-problem-title">{c.title}</div>
                <div className="v2-problem-desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BEFORE / AFTER ──────────────────────────────────── */}
      <section className="v2-ba-section">
        <div className="v2-container">
          <div style={{ textAlign: 'center', marginBottom: 0 }}>
            <div className="v2-section-tag v2-reveal">La diferencia</div>
            <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">
              Con Diana o sin Diana
            </h2>
          </div>
          <div className="v2-ba-grid">
            <div className="v2-ba-col before v2-reveal v2-reveal-delay-1">
              <div className="v2-ba-title-before">✗ Sin Diana IA</div>
              {[
                'Seguimiento manual (si alguien se acuerda)',
                'Pacientes se enfrían y no vuelven',
                'Mensajes genéricos que nadie responde',
                'Tu equipo saturado con tareas repetitivas',
                'Oportunidades perdidas invisibles',
              ].map(t => (
                <div key={t} className="v2-ba-item">
                  <span className="icon">✗</span>
                  <span className="text">{t}</span>
                </div>
              ))}
            </div>
            <div className="v2-ba-col after v2-reveal v2-reveal-delay-2">
              <div className="v2-ba-title-after">✓ Con Diana IA</div>
              {[
                'Seguimiento automático e inteligente',
                'Reactivación en el momento óptimo',
                'Mensajes personalizados con IA',
                'Tu equipo enfocado en atender pacientes',
                'Recovery Score™ que muestra el costo real de no tener seguimiento',
              ].map(t => (
                <div key={t} className="v2-ba-item">
                  <span className="icon">✓</span>
                  <span className="text">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── QUÉ DETECTA ─────────────────────────────────────── */}
      <section className="v2-detect">
        <div className="v2-container">
          <div style={{ textAlign: 'center' }}>
            <div className="v2-section-tag v2-reveal">Inteligencia</div>
            <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">
              Lo que Diana detecta<br />en tu WhatsApp
            </h2>
            <p className="v2-section-sub v2-reveal v2-reveal-delay-2" style={{ margin: '0 auto' }}>
              Diana no es un chatbot genérico. Analiza el contexto real de cada conversación para identificar exactamente dónde se están perdiendo tus citas.
            </p>
          </div>
          <div className="v2-detect-grid">
            {DETECT_CARDS.map((c, i) => (
              <div key={c.title} className={`v2-detect-card v2-reveal v2-reveal-delay-${(i % 3) + 1}`}>
                <div className="v2-icon-box">
                  <c.icon />
                </div>
                <div className="v2-detect-title">{c.title}</div>
                <div className="v2-detect-desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARATIVO ─────────────────────────────────────── */}
      <section className="v2-compare">
        <div className="v2-container">
          <div style={{ textAlign: 'center' }}>
            <div className="v2-section-tag v2-reveal">La comparación real</div>
            <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">
              Seguimiento manual, chatbot genérico o Diana
            </h2>
            <p className="v2-section-sub v2-reveal v2-reveal-delay-2" style={{ margin: '0 auto' }}>
              Estas son las tres opciones reales que tiene una clínica hoy. Así se comparan.
            </p>
          </div>

          <div className="v2-compare-table v2-reveal v2-reveal-delay-2">
            <div className="v2-compare-row v2-compare-head">
              <div className="v2-compare-label" />
              <div className="v2-compare-col-head">Seguimiento manual</div>
              <div className="v2-compare-col-head">Chatbot genérico</div>
              <div className="v2-compare-col-head diana">Diana IA</div>
            </div>
            {COMPARE_ROWS.map(row => (
              <div key={row.label} className="v2-compare-row">
                <div className="v2-compare-label">{row.label}</div>
                <div className="v2-compare-cell">{row.manual ? '✓' : '✗'}</div>
                <div className="v2-compare-cell">{row.bot ? '✓' : '✗'}</div>
                <div className="v2-compare-cell diana">{row.diana ? '✓' : '✗'}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ───────────────────────────────────── */}
      <section className="v2-section" id="como-funciona">
        <div className="v2-container">
          <div style={{ textAlign: 'center', marginBottom: 0 }}>
            <div className="v2-section-tag v2-reveal">Proceso</div>
            <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">
              Activo en menos de 5 minutos
            </h2>
            <p className="v2-section-sub v2-reveal v2-reveal-delay-2" style={{ margin: '0 auto' }}>
              Sin instalaciones, sin conocimientos técnicos. Solo conectas y Diana empieza a trabajar.
            </p>
          </div>
          <div className="v2-steps-grid">
            {STEPS.map((s, i) => (
              <div key={s.n} className={`v2-step v2-reveal v2-reveal-delay-${i + 1}`}>
                <div className="v2-step-num">{s.n}</div>
                <div className="v2-step-title">{s.title}</div>
                <div className="v2-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RECOVERY CALCULATOR ─────────────────────────────── */}
      <section className="v2-calc-section">
        <div className="v2-container">
          <div style={{ textAlign: 'center' }}>
            <div className="v2-section-tag v2-reveal">Calculadora</div>
            <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">
              ¿Cuánto estás perdiendo<br />cada semana?
            </h2>
            <p className="v2-section-sub v2-reveal v2-reveal-delay-2" style={{ margin: '0 auto' }}>
              Estima tus oportunidades perdidas con el mismo análisis que Diana aplica a conversaciones reales.
            </p>
          </div>
          <div className="v2-reveal v2-reveal-delay-2">
            <RecoveryCalc />
          </div>
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <p style={{ fontSize: 12, color: 'var(--text-faint)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 20 }}>
              — o, si quieres el score real en vez del estimado —
            </p>
            <Link to="/register" className="v2-pulse-btn" onClick={handleCTA}>
              Ver mi análisis real →
            </Link>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10 }}>
              Conecta tu WhatsApp: Diana calcula tu Recovery Score™ con tus conversaciones reales, no una estimación.
            </p>
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────── */}
      <section className="v2-pricing" id="precio">
        <div className="v2-container" style={{ textAlign: 'center' }}>
          <div className="v2-section-tag v2-reveal">Planes</div>
          <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">
            Un precio directo.<br />Sin sorpresas.
          </h2>
          <p className="v2-section-sub v2-reveal v2-reveal-delay-2" style={{ margin: '0 auto' }}>
            Empieza con 14 días de prueba completa, sin tarjeta de crédito.
          </p>

          {/* Toggle */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="v2-billing-toggle v2-reveal v2-reveal-delay-3">
              <button
                className={`v2-billing-opt ${billing === 'monthly' ? 'active' : ''}`}
                onClick={() => setBilling('monthly')}
              >Mensual</button>
              <button
                className={`v2-billing-opt ${billing === 'annual' ? 'active' : ''}`}
                onClick={() => setBilling('annual')}
              >
                Anual &nbsp;<span className="v2-billing-badge">2 meses gratis</span>
              </button>
            </div>
          </div>

          <div className="v2-plans-grid">
            {/* Básico */}
            <div className="v2-plan-card v2-reveal v2-reveal-delay-1">
              <div className="v2-plan-name">Básico</div>
              <div className="v2-plan-price">
                <span className="v2-plan-price-num">${prices.basic}</span>
                <span className="v2-plan-price-period">/mes</span>
              </div>
              <div className="v2-plan-desc">
                Automatiza respuestas y agenda. Ideal para clínicas que empiezan con IA.
              </div>
              <div className="v2-plan-features">
                {BASIC_FEATS.map(f => (
                  <div key={f} className="v2-plan-feature">
                    <span className="chk">✓</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <Link to="/register" className="v2-plan-cta ghost" onClick={handleCTA}>
                Empezar prueba gratis
              </Link>
            </div>

            {/* Pro */}
            <div className="v2-plan-card popular v2-reveal v2-reveal-delay-2">
              <div className="v2-popular-badge">Más popular</div>
              <div className="v2-plan-name">Pro</div>
              <div className="v2-plan-price">
                <span className="v2-plan-price-num">${prices.pro}</span>
                <span className="v2-plan-price-period">/mes</span>
              </div>
              <div className="v2-plan-desc">
                Recupera oportunidades y optimiza el seguimiento. Para clínicas que quieren crecer.
              </div>
              <div className="v2-plan-features">
                {PRO_FEATS.map(f => (
                  <div key={f} className="v2-plan-feature">
                    <span className="chk">✓</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <Link to="/register" className="v2-plan-cta primary" onClick={handleCTA}>
                Empezar prueba gratis
              </Link>
            </div>
          </div>

          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 24 }}>
            Prueba gratis 14 días o 100 contactos únicos
          </p>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section className="v2-faq" id="faq">
        <div className="v2-container" style={{ textAlign: 'center' }}>
          <div className="v2-section-tag v2-reveal">Preguntas frecuentes</div>
          <h2 className="v2-section-title v2-reveal v2-reveal-delay-1">Todo lo que necesitas saber</h2>
          <div className="v2-faq-list">
            {FAQS.map((f, i) => (
              <div key={f.q} className="v2-reveal v2-reveal-delay-1">
                <FaqItem
                  q={f.q} a={f.a}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ───────────────────────────────────────── */}
      <section className="v2-cta-final">
        <div className="v2-container">
          <div className="v2-cta-final-inner">
            <div className="v2-cta-final-eyebrow v2-reveal">Diana Recovery Score™</div>
            <h2 className="v2-cta-final-title v2-reveal v2-reveal-delay-1">
              Ve cuánto te cuesta hoy<br />
              <em><span className="v2-aurora-text">no tener seguimiento automático</span></em>
            </h2>
            <p className="v2-cta-final-sub v2-reveal v2-reveal-delay-2">
              Conecta tu WhatsApp en 5 minutos. Diana analiza tus conversaciones recientes, calcula tu Recovery Score™ y empieza a trabajar desde ese momento.
            </p>
            <p className="v2-cta-final-sub v2-reveal v2-reveal-delay-2" style={{ fontSize: 13, marginTop: -20 }}>
              El Recovery Score™ mide qué tan bien una clínica responde y le da seguimiento a sus pacientes por WhatsApp. Así les está yendo, en promedio, a las que todavía no usan Diana:
            </p>

            {/* Score visual */}
            <div className="v2-score-display v2-reveal v2-reveal-delay-2">
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: 8 }}>
                  Puntaje promedio de clínicas sin Diana
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span className="v2-score-num">38</span>
                  <span style={{ color: 'var(--text-faint)', fontSize: 16, fontWeight: 600 }}>/100</span>
                </div>
                <div style={{ color: 'var(--gold)', fontSize: 12, fontWeight: 700, marginTop: 4 }}>EN RIESGO</div>
              </div>
              <div className="v2-score-divider" />
              <div className="v2-score-stat">
                <div className="v2-score-stat-num">12–18</div>
                <div className="v2-score-stat-label">conversaciones sin seguimiento / semana</div>
              </div>
              <div className="v2-score-divider" />
              <div className="v2-score-stat">
                <div className="v2-score-stat-num highlight">5–8</div>
                <div className="v2-score-stat-label">conversaciones que Diana puede retomar / semana</div>
              </div>
            </div>

            <div className="v2-reveal v2-reveal-delay-3">
              <Link to="/register" className="v2-pulse-btn lg" onClick={handleCTA}>
                Conectar mi WhatsApp y ver el Score™ →
              </Link>
            </div>
            <p className="v2-cta-final-note v2-reveal v2-reveal-delay-4">
              14 días gratis · 100 contactos únicos · Sin tarjeta de crédito
            </p>
            <p className="v2-cta-final-note v2-reveal v2-reveal-delay-4" style={{ marginTop: 2 }}>
              🔒 Tus datos y los de tus pacientes permanecen privados
            </p>
          </div>
        </div>
      </section>

      </main>

      {/* ── FOOTER ──────────────────────────────────────────── */}
      <footer className="v2-footer">
        <div className="v2-container">
          <div className="v2-footer-inner">
            <div className="v2-footer-logo">
              <img src="/logo.webp" alt="Diana IA" width={26} height={26} />
              <span>Diana IA</span>
            </div>
            <div className="v2-footer-links">
              <Link to="/terms">Términos</Link>
              <Link to="/privacy">Privacidad</Link>
              <Link to="/refund">Reembolsos</Link>
              <a href="mailto:soporte@dianaia.online">Soporte</a>
              <a href="https://www.instagram.com/abrahamconia/" target="_blank" rel="noopener noreferrer">Instagram</a>
            </div>
            <div className="v2-footer-copy">© {new Date().getFullYear()} Diana IA. Todos los derechos reservados.</div>
          </div>
        </div>
      </footer>

    </div>
  );
}
